// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react'
import { credits, dollars } from 'Utils/numberFormat';
import { Page, Text, View, Document, Image } from '@react-pdf/renderer';
import logo from 'Assets/aw-horizontal-navy.png';
import infoIcon from 'Assets/info-icon.png';
import styles from './styles';

interface PDFData {
  generatedOn: string;
  orgName: string;
  // the period every figure counts, the header's own (EXP-36)
  period: string;
  creditsProcessedToDate: number;
  // the balance's used credits, beside the credits it holds
  balanceUsed: number;
  avgCreditsPerMonth: number;
  avgCreditsPerProject: number;
  remainingUsableCredits: number;
  contractExpirationDate: string;
  pendingContractValue: number;
  projectsPassedToDate: number;
  avgProjectsPerMonth: number;
  purchasedCredits: number;
  chartImages: {
    credits?: string;
    projects?: string;
    logins?: string;
    calendar?: string;
  };
  timeOption: string;
  selectedYear: string;
}

interface PDFProps {
  data: PDFData;
}

// a credit figure as text: two decimals, always shown
const formatNumber = (number: number) => credits(Number(number));

const PlanUsagePDF = ({ data }: PDFProps) => {
  const {
    generatedOn,
    orgName,
    period,
    creditsProcessedToDate,
    balanceUsed,
    avgCreditsPerMonth,
    avgCreditsPerProject,
    remainingUsableCredits,
    contractExpirationDate,
    projectsPassedToDate,
    avgProjectsPerMonth,
    purchasedCredits,
    chartImages,
    timeOption,
    pendingContractValue,
    selectedYear,
  } = data;

  return (
    <Document style={{ width: '100%', padding: '45px' }}>
      <Page size="LETTER">
        <View style={styles.container}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Image src={logo} style={styles.logo} />
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.text1}>Generated on: </Text>
              <Text style={styles.text2}>{generatedOn}</Text>
            </View>
          </View>
          <View style={styles.content}>
            <View style={styles.package}>
              <Text style={styles.title1}>Plan Usage For {orgName || 'Company'}</Text>
              <Text style={styles.text1}>{period}</Text>
              <View style={styles.detailsContainer}>
                <View style={styles.detailsLeft}>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Credits Processed </Text>
                    <Text style={styles.detailsLeftItemValue}>{formatNumber(creditsProcessedToDate)}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Avg Credits Per Month </Text>
                    <Text style={styles.detailsLeftItemValue}>{formatNumber(avgCreditsPerMonth)}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Avg Credits Per Project </Text>
                    <Text style={styles.detailsLeftItemValue}>{formatNumber(avgCreditsPerProject)}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Remaining usable credits </Text>
                    <Text style={styles.detailsLeftItemValue}>{formatNumber(remainingUsableCredits)}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Contract Expiration Date </Text>
                    <Text style={styles.detailsLeftItemValue}>{contractExpirationDate}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Pending Contract Value </Text>
                    <Text style={styles.detailsLeftItemValue}>{dollars(pendingContractValue)}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Projects Processed </Text>
                    <Text style={styles.detailsLeftItemValue}>{projectsPassedToDate}</Text>
                  </View>
                  <View style={styles.detailsLeftItemView}>
                    <Text style={styles.detailsLeftItemLabel}>Avg. Projects Per Month </Text>
                    <Text style={styles.detailsLeftItemValue}>{avgProjectsPerMonth.toFixed(2)}</Text>
                  </View>
                </View>
                <View style={styles.detailsRight}>
                  <View style={styles.titleContainer}>
                    <Image src={infoIcon} style={styles.icon} />
                    <Text style={styles.titleText}>Credits Usage</Text>
                  </View>
                  <View style={styles.detailsRightContent}>
                    <Text style={styles.creditsUsed}>{formatNumber(balanceUsed)}</Text>
                    <Text style={styles.fromCredits}>{`of ${formatNumber(purchasedCredits)} Credits`}</Text>
                    <Text style={styles.text1}>used</Text>
                  </View>
                  <View
                    style={styles.detailsRightDivider}
                  />
                  <View style={styles.detailsRightFooter}>
                    <Text style={styles.creditsRemainingValue}>{formatNumber(remainingUsableCredits || 0)}</Text>
                    <Text style={styles.text1}>credits remaining</Text>
                  </View>
                </View>
              </View>
              <View style={styles.graphContainer}>
                <View style={styles.graph}>
                  <Text style={styles.graphTitle}>Credits Per {timeOption.slice(0, -2)} ({selectedYear})</Text>
                  <View style={styles.graphImageContainer}>
                  {chartImages?.credits && (
                    <Image 
                      src={chartImages.credits} 
                      style={styles.graphImage} 
                    />
                  )}
                  </View>
                </View>
                <View style={styles.graph}>
                  <Text style={styles.graphTitle}>{`Projects Per Month (${selectedYear})`}</Text>
                  <View style={styles.graphImageContainer}>
                    {chartImages?.projects && (
                      <Image 
                        src={chartImages.projects} 
                        style={styles.graphImage} 
                      />
                    )}
                  </View>
                </View>
                <View style={styles.graph}>
                  <Text style={styles.graphTitle}>Logins Per Month</Text>
                  <View style={styles.graphImageContainer}>
                    {chartImages?.logins && (
                      <Image 
                        src={chartImages.logins} 
                        style={styles.graphImage} 
                      />
                    )}
                  </View>
                </View>
                {chartImages?.calendar && (
                  <View style={styles.graph}>
                    <Text style={styles.graphTitle}>Logins This Month</Text>
                    <View style={styles.graphImageContainer}>
                      <Image 
                        src={chartImages.calendar} 
                        style={styles.calendarImage} 
                      />
                    </View>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  )
}

export default PlanUsagePDF;
